<script setup lang="ts">
import { ref, onMounted, inject, Ref, computed, onUnmounted } from 'vue'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge, type BadgeVariants } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import TreeViewSelect from '@/components/TreeViewSelect.vue'
import { toast } from 'vue-sonner'
import { VhostInfo } from '@/types/Vhost'
import { useMLTraining, type TrainingStats, type TrainingRun, type MLModelInfo } from '@/services/MLTraining'
import { useGroups } from '@/services/Groups'
import {
	Database,
	Brain,
	Play,
	RefreshCw,
	CheckCircle2,
	XCircle,
	Clock,
	AlertTriangle,
	TrendingUp,
	Loader2,
	Info,
	BarChart3,
	Layers,
	Zap,
} from 'lucide-vue-next'

const vhostInfo = inject<Ref<VhostInfo> | null>('vhostInfo', null)
const vhostId = inject<Ref<string> | null>('vhostId', null)

const { getTrainingStats, getTrainingRuns, startTraining, getModelInfo, checkTrainingStatus } = useMLTraining()
const { groupTree, getGroupTree } = useGroups()

const stats = ref<TrainingStats>({
	totalSamples: 0,
	realCount: 0,
	fakeCount: 0,
	unexportedCount: 0,
	lastTrainingDate: null,
	lastAccuracy: null,
})
const runs = ref<TrainingRun[]>([])
const modelInfo = ref<MLModelInfo>({ hasCustomModel: false, models: null, lastTrained: null })
const loading = ref(true)
const trainingInProgress = ref(false)
const selectedGroupId = ref<string>('')
const pollInterval = ref<ReturnType<typeof setInterval> | null>(null)

const vid = computed(() => vhostId?.value ? Number(vhostId.value) : 0)

const accuracyPercent = computed(() => {
	if (stats.value.lastAccuracy !== null) {
		return (stats.value.lastAccuracy * 100).toFixed(1)
	}
	return null
})

const sampleBalance = computed(() => {
	const total = stats.value.realCount + stats.value.fakeCount
	if (total === 0) return { realPct: 0, fakePct: 0, balanced: true }
	const realPct = Math.round((stats.value.realCount / total) * 100)
	const fakePct = 100 - realPct
	const balanced = Math.abs(realPct - fakePct) < 30
	return { realPct, fakePct, balanced }
})

const canTrain = computed(() => {
	return stats.value.unexportedCount >= 20 && !trainingInProgress.value
})

const loadData = async () => {
	if (!vid.value) return
	loading.value = true
	try {
		const [statsResult, runsResult, modelResult] = await Promise.all([
			getTrainingStats(vid.value),
			getTrainingRuns(vid.value),
			getModelInfo(vid.value),
			getGroupTree(String(vid.value)),
		])
		stats.value = statsResult
		runs.value = runsResult
		modelInfo.value = modelResult

		// Check if any run is currently in progress
		const activeRun = runsResult.find((r: TrainingRun) => ['pending', 'exporting', 'training'].includes(r.status))
		trainingInProgress.value = !!activeRun
	} catch (err) {
		console.error('Failed to load ML training data:', err)
		toast('Error', { description: 'Failed to load training data.' })
	} finally {
		loading.value = false
	}
}

const handleStartTraining = async () => {
	if (!vid.value || trainingInProgress.value) return
	trainingInProgress.value = true

	const groupId = selectedGroupId.value ? Number(selectedGroupId.value) : undefined
	const result = await startTraining(vid.value, groupId)
	if (result.success) {
		toast('Training Started', { description: result.message || 'Model training has been initiated.' })
		startPolling()
	} else {
		toast('Training Failed', { description: result.message || 'Could not start training.' })
		trainingInProgress.value = false
	}
}

const startPolling = () => {
	if (pollInterval.value) return
	pollInterval.value = setInterval(async () => {
		if (!vid.value) return
		const status = await checkTrainingStatus(vid.value)
		if (status.status === 'completed' || status.status === 'failed') {
			trainingInProgress.value = false
			stopPolling()
			await loadData()
			if (status.status === 'completed') {
				toast('Training Complete', { description: 'Your custom model has been updated.' })
			} else {
				toast('Training Failed', { description: status.error || 'Training encountered an error.' })
			}
		}
	}, 10000)
}

const stopPolling = () => {
	if (pollInterval.value) {
		clearInterval(pollInterval.value)
		pollInterval.value = null
	}
}

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) return '—'
	const d = new Date(dateStr)
	return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const statusBadgeVariant = (status: string): BadgeVariants['variant'] => {
	switch (status) {
		case 'completed': return 'default'
		case 'failed': return 'destructive'
		case 'training': return 'secondary'
		case 'exporting': return 'secondary'
		case 'pending': return 'outline'
		default: return 'outline'
	}
}

onMounted(() => {
	loadData()
	if (trainingInProgress.value) {
		startPolling()
	}
})

onUnmounted(() => {
	stopPolling()
})
</script>

<template>
	<div class="p-4 space-y-6">
		<!-- Refresh -->
		<div class="flex justify-end">
			<Button variant="outline" size="sm" @click="loadData" :disabled="loading">
				<RefreshCw class="h-4 w-4 mr-2" :class="{ 'animate-spin': loading }" />
				Refresh
			</Button>
		</div>

		<!-- Loading State -->
		<div v-if="loading" class="flex items-center justify-center py-20">
			<Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
		</div>

		<template v-else>
			<!-- Stats Cards -->
			<div class="grid gap-4 md:grid-cols-4">
				<Card>
					<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle class="text-sm font-medium">Total Samples</CardTitle>
						<Database class="h-5 w-5 text-blue-500" />
					</CardHeader>
					<CardContent>
						<div class="text-2xl font-bold">{{ stats.totalSamples.toLocaleString() }}</div>
						<p class="text-xs text-muted-foreground">Labeled media items</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle class="text-sm font-medium">Real / Fake</CardTitle>
						<BarChart3 class="h-5 w-5 text-purple-500" />
					</CardHeader>
					<CardContent>
						<div class="text-2xl font-bold">
							<span class="text-green-600">{{ stats.realCount }}</span>
							<span class="text-muted-foreground mx-1">/</span>
							<span class="text-red-600">{{ stats.fakeCount }}</span>
						</div>
						<p class="text-xs text-muted-foreground">
							<span v-if="sampleBalance.balanced" class="text-green-600">Balanced</span>
							<span v-else class="text-yellow-600">{{ sampleBalance.realPct }}% real / {{ sampleBalance.fakePct }}% fake</span>
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle class="text-sm font-medium">Ready to Train</CardTitle>
						<Layers class="h-5 w-5 text-orange-500" />
					</CardHeader>
					<CardContent>
						<div class="text-2xl font-bold">{{ stats.unexportedCount }}</div>
						<p class="text-xs text-muted-foreground">New samples since last run</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle class="text-sm font-medium">Model Accuracy</CardTitle>
						<TrendingUp class="h-5 w-5 text-green-500" />
					</CardHeader>
					<CardContent>
						<div class="text-2xl font-bold">
							<template v-if="accuracyPercent !== null">{{ accuracyPercent }}%</template>
							<template v-else>—</template>
						</div>
						<p class="text-xs text-muted-foreground">
							<template v-if="stats.lastTrainingDate">Last trained {{ formatDate(stats.lastTrainingDate) }}</template>
							<template v-else>No training runs yet</template>
						</p>
					</CardContent>
				</Card>
			</div>

			<!-- Custom Model Status -->
			<Card v-if="modelInfo.hasCustomModel">
				<CardHeader>
					<div class="flex items-center gap-2">
						<Zap class="h-5 w-5 text-yellow-500" />
						<CardTitle class="text-lg">Custom Model Active</CardTitle>
					</div>
					<CardDescription>
						Your custom-trained model is being used alongside the base models for content authenticity detection.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div class="grid gap-3 md:grid-cols-3">
						<div v-for="(info, name) in modelInfo.models" :key="name" class="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
							<span class="text-sm font-medium">{{ name }}</span>
							<Badge variant="outline">{{ typeof info === 'object' && info?.accuracy ? (info.accuracy * 100).toFixed(1) + '%' : 'Active' }}</Badge>
						</div>
					</div>
				</CardContent>
			</Card>

			<!-- Training Controls -->
			<Card>
				<CardHeader>
					<div class="flex items-center gap-2">
						<Brain class="h-5 w-5 text-indigo-500" />
						<CardTitle class="text-lg">Start Training Run</CardTitle>
					</div>
					<CardDescription>
						Fine-tune the detection models using your labeled moderation data. Minimum 20 new samples required.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div class="flex items-end gap-4">
						<div class="flex-1 max-w-xs">
							<label class="text-sm font-medium mb-2 block">Group Filter (optional)</label>
							<TreeViewSelect
								v-model="selectedGroupId"
								:group-tree="groupTree"
							/>
						</div>
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger as-child>
									<div>
										<Button
											@click="handleStartTraining"
											:disabled="!canTrain"
											class="gap-2"
										>
											<template v-if="trainingInProgress">
												<Loader2 class="h-4 w-4 animate-spin" />
												Training in Progress...
											</template>
											<template v-else>
												<Play class="h-4 w-4" />
												Start Training
											</template>
										</Button>
									</div>
								</TooltipTrigger>
								<TooltipContent v-if="!canTrain && !trainingInProgress">
									<p v-if="stats.unexportedCount < 20">Need at least 20 new samples (currently {{ stats.unexportedCount }})</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>

					<div v-if="trainingInProgress" class="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg flex items-center gap-3">
						<Loader2 class="h-5 w-5 animate-spin text-blue-500" />
						<div>
							<p class="text-sm font-medium text-blue-700 dark:text-blue-300">Training in progress</p>
							<p class="text-xs text-blue-600 dark:text-blue-400">This may take several minutes. You can leave this page and return later.</p>
						</div>
					</div>
				</CardContent>
			</Card>

			<!-- Training History -->
			<Card>
				<CardHeader>
					<CardTitle class="text-lg">Training History</CardTitle>
					<CardDescription>Previous training runs and their results</CardDescription>
				</CardHeader>
				<CardContent>
					<div v-if="runs.length === 0" class="text-center py-10 text-muted-foreground">
						<Brain class="h-10 w-10 mx-auto mb-3 opacity-40" />
						<p>No training runs yet</p>
						<p class="text-sm mt-1">Start collecting moderation decisions to build your training dataset.</p>
					</div>

					<div v-else class="relative overflow-x-auto">
						<table class="w-full text-sm text-left">
							<thead class="text-xs text-muted-foreground uppercase border-b">
								<tr>
									<th class="px-4 py-3">Run</th>
									<th class="px-4 py-3">Status</th>
									<th class="px-4 py-3">Samples</th>
									<th class="px-4 py-3">Accuracy</th>
									<th class="px-4 py-3">Best Epoch</th>
									<th class="px-4 py-3">Started</th>
									<th class="px-4 py-3">Completed</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="run in runs" :key="run.id" class="border-b last:border-0">
									<td class="px-4 py-3 font-medium">#{{ run.id }}</td>
									<td class="px-4 py-3">
										<Badge :variant="statusBadgeVariant(run.status)">
											<CheckCircle2 v-if="run.status === 'completed'" class="h-3 w-3 mr-1" />
											<XCircle v-else-if="run.status === 'failed'" class="h-3 w-3 mr-1" />
											<Loader2 v-else-if="run.status === 'training' || run.status === 'exporting'" class="h-3 w-3 mr-1 animate-spin" />
											<Clock v-else class="h-3 w-3 mr-1" />
											{{ run.status }}
										</Badge>
									</td>
									<td class="px-4 py-3">
										<span class="text-green-600">{{ run.realCount }}</span>
										<span class="text-muted-foreground mx-1">/</span>
										<span class="text-red-600">{{ run.fakeCount }}</span>
									</td>
									<td class="px-4 py-3">
										<span v-if="run.accuracy !== null" class="font-medium">{{ (run.accuracy * 100).toFixed(1) }}%</span>
										<span v-else class="text-muted-foreground">—</span>
									</td>
									<td class="px-4 py-3">
										<span v-if="run.bestEpoch !== null">{{ run.bestEpoch }}</span>
										<span v-else class="text-muted-foreground">—</span>
									</td>
									<td class="px-4 py-3 text-muted-foreground">{{ formatDate(run.startedAt) }}</td>
									<td class="px-4 py-3 text-muted-foreground">{{ formatDate(run.completedAt) }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			<!-- How It Works -->
			<Card>
				<CardHeader>
					<div class="flex items-center gap-2">
						<Info class="h-5 w-5 text-muted-foreground" />
						<CardTitle class="text-lg">How It Works</CardTitle>
					</div>
				</CardHeader>
				<CardContent>
					<div class="grid gap-6 md:grid-cols-3">
						<div class="flex gap-3">
							<div class="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">1</div>
							<div>
								<p class="font-medium text-sm">Collect</p>
								<p class="text-xs text-muted-foreground mt-1">Every moderation decision (approve/deny) automatically labels media as real or AI-generated, building your training dataset.</p>
							</div>
						</div>
						<div class="flex gap-3">
							<div class="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-purple-600 dark:text-purple-300 text-sm font-bold">2</div>
							<div>
								<p class="font-medium text-sm">Train</p>
								<p class="text-xs text-muted-foreground mt-1">When you have enough new samples, start a training run to fine-tune the ML models on your specific content patterns.</p>
							</div>
						</div>
						<div class="flex gap-3">
							<div class="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-green-600 dark:text-green-300 text-sm font-bold">3</div>
							<div>
								<p class="font-medium text-sm">Deploy</p>
								<p class="text-xs text-muted-foreground mt-1">The custom model is automatically used alongside the base models, improving detection accuracy for your specific use case.</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</template>
	</div>
</template>
